import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Price, PriceInput, Textarea } from '@artosera/ui';
import { useSaisirReversementArtiste, type CorpsReversement } from '../../api/comptesArtistes.js';
import { Field } from '../Field.js';
import { estDepassement, messageCompte } from './compteUi.js';
import styles from './ComptesArtistes.module.css';

export function ReversementDialog({
  galerieSlug,
  artisteId,
  montantPropose,
  onFermer,
}: {
  galerieSlug: string;
  artisteId: string;
  montantPropose: number;
  onFermer: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const saisir = useSaisirReversementArtiste(galerieSlug);
  const [cle] = useState(() => crypto.randomUUID());
  const [montant, setMontant] = useState(montantPropose);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [moyen, setMoyen] = useState('');
  const [reference, setReference] = useState('');
  const [motif, setMotif] = useState('');
  const [refuse, setRefuse] = useState<CorpsReversement | null>(null);
  const [erreur, setErreur] = useState('');
  async function soumettre() {
    setErreur('');
    const corps: CorpsReversement = refuse
      ? { ...refuse, confirmerDepassement: true, motif }
      : { artisteId, montantCentimes: montant, date, moyen, reference, cle };
    try {
      await saisir.mutateAsync(corps);
      onFermer();
    } catch (e) {
      if (estDepassement(e) && !refuse) setRefuse(corps);
      else setErreur(messageCompte(e, t('commun:erreurReseau')));
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v && !saisir.isPending) onFermer();
      }}
      title={label(refuse ? 'confirmerDepassement' : 'saisirReversement')}
      description={label(refuse ? 'depassementAide' : 'reversementAide')}
      closeLabel={t('commun:fermer')}
    >
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          void soumettre();
        }}
      >
        {refuse ? (
          <>
            <p>
              <Price cents={refuse.montantCentimes} lang={langue} /> · {refuse.date} ·{' '}
              {refuse.moyen} · {refuse.reference}
            </p>
            <Field htmlFor="reversement-motif" label={label('motif')}>
              <Textarea
                id="reversement-motif"
                required
                maxLength={500}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
              />
            </Field>
          </>
        ) : (
          <>
            <Field htmlFor="reversement-montant" label={label('montant')}>
              <PriceInput
                id="reversement-montant"
                cents={montant}
                onCentsChange={setMontant}
                lang={langue}
                required
              />
            </Field>
            <Field htmlFor="reversement-date" label={label('date')}>
              <Input
                id="reversement-date"
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
            <Field htmlFor="reversement-moyen" label={label('moyen')}>
              <Input
                id="reversement-moyen"
                required
                maxLength={120}
                value={moyen}
                onChange={(e) => setMoyen(e.target.value)}
              />
            </Field>
            <Field htmlFor="reversement-reference" label={label('reference')}>
              <Input
                id="reversement-reference"
                maxLength={200}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </Field>
          </>
        )}
        {erreur && <p role="alert">{erreur}</p>}
        <Button
          type="submit"
          loading={saisir.isPending}
          disabled={refuse ? !motif.trim() : montant <= 0 || !date || !moyen.trim()}
        >
          {label(refuse ? 'confirmerDepassement' : 'enregistrerReversement')}
        </Button>
      </form>
    </Dialog>
  );
}
